// ---------------------------------------------------------------------------
// api-log-classification.ts — is this failed request a FAULT, or just traffic?
// ---------------------------------------------------------------------------
//
// `api-client` logged every non-2xx at ERROR level, so an anonymous visitor
// receiving a correct 401 was recorded exactly like a 500. Production's error
// log filled with entries that were all working-as-designed:
//
//   HotelStack  12/12 errors — bingbot crawling /about, /ar → /auth/me 401
//   BookMyRoom  18/18 errors — visitors on the homepage → /guest/bookings 401
//
// Both verified against the live endpoints as the correct response for a
// caller with no session. A log where every entry is expected is a log nobody
// reads, which is how a real fault goes unnoticed.
//
// So the split is NOT by status code. A 401 is expected for a visitor and
// alarming for a signed-in user whose session just died — routing all 401s to
// an access log would hide that. The question is whether the outcome was
// EXPECTED given who was asking.
// ---------------------------------------------------------------------------

/** Where a failed request's log entry belongs. */
export type ApiLogDestination = 'access' | 'error';

export interface ApiFailureContext {
  status: number;
  path: string;
  /** Whether the app believed it had a session when it made the call. */
  authenticated: boolean;
  /**
   * HTTP method. Optional, defaulting to GET, so existing call sites keep
   * compiling — but supply it where you can: an anonymous 401 on a write is
   * treated as a fault, and omitting the method silently forgoes that.
   */
  method?: string;
  /** The server's error `code`, when the body carried one. */
  code?: string;
}

/**
 * Codes for a gateway rejecting credentials the user just typed — the answer
 * the settings screen exists to show, not a fault in the app.
 */
// PaymentHooks' verdicts on a key the user typed come back under its own codes:
// UNAUTHORIZED (key rejected) and MISSING_SCOPES (valid key, wrong permissions).
const USER_INPUT_CREDENTIAL_CODES = new Set(['INVALID_KEY_ID', 'INVALID_CREDENTIALS', 'UNAUTHORIZED', 'MISSING_SCOPES', 'INVALID_KEY']);

/**
 * Custom-checkout-domain refusals caused by what the owner entered or has not
 * set up yet: the hostname, or no PaymentHooks account connected (the card says
 * "connect it first"; the account can also be removed while the page is open).
 */
const USER_INPUT_DOMAIN_CODES = new Set(['INVALID_HOSTNAME', 'HOSTNAME_REQUIRED', 'DUPLICATE', 'PAYMENTHOOKS_NOT_CONFIGURED', 'DOMAIN_NOT_ACTIVE']);

/**
 * Endpoints whose whole job is answering "is anyone signed in?".
 *
 * A negative answer is the expected result for a visitor, not a failure. These
 * are matched as prefixes because the client may append query strings.
 */
const SESSION_PROBE_PATHS = ['/auth/me', '/auth/refresh', '/auth/session'];

function isSessionProbe(path: string): boolean {
  const clean = path.split('?')[0];
  return SESSION_PROBE_PATHS.some((p) => clean === p || clean.startsWith(`${p}/`));
}

/**
 * Decide where a failed API call should be logged.
 *
 * Deliberately conservative: anything not provably routine stays an error.
 * Widening this filter is how a real fault gets buried, so the default for an
 * unrecognised case is `error`.
 */
export function classifyApiFailure(ctx: ApiFailureContext): ApiLogDestination {
  // A server fault is always a fault, whoever asked.
  if (ctx.status >= 500) return 'error';

  // 404 and 405 on a call the UI itself made mean the UI is wired to an
  // endpoint that cannot answer it. That exact defect shipped repeatedly —
  // /hotel/pos/menu answered 405 while the editor reported success, and three
  // room-service routes did the same. Routing these to an access log would
  // blind us to the next one.
  if (ctx.status === 404 || ctx.status === 405) return 'error';

  if (ctx.status === 401 || ctx.status === 403) {
    // Signed in and refused: the session broke, or an entitlement is wrong.
    // Either is a real problem and belongs in the error log.
    if (ctx.authenticated) return 'error';

    // A write refused is a user losing work, even anonymously — it means the
    // UI offered an action it could not complete.
    const method = (ctx.method ?? 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') return 'error';

    // No session, correctly refused — this is what 401 is FOR.
    return 'access';
  }

  // `POST /auth/refresh` with no refresh token answers 400: there is nothing
  // to refresh. Expected for a visitor; a genuine fault once signed in.
  if (ctx.status === 400 && isSessionProbe(ctx.path) && !ctx.authenticated) {
    return 'access';
  }

  // A payment gateway judging credentials the user typed. "Razorpay rejected
  // these keys" is the screen doing its job — the user sees why and fixes it.
  // Matched on the exact codes, not on the path: any OTHER 400 from these
  // endpoints is still a UI defect and stays in the error log.
  if (ctx.status === 400 && ctx.code && USER_INPUT_CREDENTIAL_CODES.has(ctx.code)) {
    return 'access';
  }

  // PaymentHooks judging a domain the user typed ("already connected to an
  // account"). Shown on the domain card; exact codes only, as above.
  if ((ctx.status === 400 || ctx.status === 409) && ctx.code && USER_INPUT_DOMAIN_CODES.has(ctx.code)) {
    return 'access';
  }

  // Everything else — 400s on real endpoints, 409s, 422s — is a fault worth
  // seeing. A validation error the UI triggered is a UI bug.
  return 'error';
}
